import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ErrorMessage,
  Form,
  Formik,
  FormikHelpers,
  FormikValues
} from 'formik';
import { initialValues, validation } from './form';
import { RootState } from 'redux/store';
import {
  ITSkillPopup,
  InitialValueInterface,
  InitialValueData
} from 'core/interface/itSkill';
import FormField from 'components/FormField/Index';
import { InputType } from 'core/enum/inputType';
import Level from 'components/Level/Index';
import ActionButton from 'components/ActionButton/Index';
import Button from 'components/Button/Index';
import Image from 'components/image/Image';
import { FormValues } from 'core/interface/form';
import { CandidateService } from 'services/candidate/candidate';
import { SkillService } from 'services/skills/skillService';
import { IAutocompleteItem } from 'core/interface/autocompleteItem';
import Autocomplete from '../Autocomplete/Index';
import { IMe } from 'core/interface/me';

import './Index.scss';

const ITSkills: FC<ITSkillPopup> = ({
  t,
  skill,
  handleAddExtraSkill,
  showAddButton,
  skillType,
  handleClose
}): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const [initialValue, setInitialValue] =
    useState<InitialValueInterface>(initialValues);
  const [currentFormValues, setCurrentFormValues] = useState<FormikValues>();
  const me = useSelector((state: IMe) => state.user).user;
  const { computerSkills } = useSelector(
    (state: RootState) => state.computerSkills
  );

  useEffect(() => {
    getSkills('');
  }, []);

  useEffect(() => {
    const newInitial: InitialValueInterface = {
      skills: skill?.map((item) => ({
        id: item.id,
        computerSkill: item.skill.name,
        description: item.description,
        proficiency: item.proficiency
      }))
    };
    setInitialValue(newInitial);
  }, [skill]);

  const handleSubmit = async (
    values: FormValues,
    actions: FormikHelpers<FormValues>
  ) => {
    setLoading(true);
    try {
      const skillPromises = values.skills.map(
        async (item: InitialValueData) => {
          const body = {
            description: item.description,
            proficiency: item.proficiency,
            skill: item.id
          };
          await SkillService.addITSkill(me.candidate_id, body);
        }
      );

      await Promise.all(skillPromises);
      await CandidateService.getCandidate(me.candidate_id);
      handleClose && handleClose();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      actions.resetForm();
    }
  };

  const handleAddSkill = () => {
    if (handleAddExtraSkill) {
      handleAddExtraSkill(currentFormValues);
    }
  };
  const getSkills = (query: string = ''): void => {
    SkillService.getITSkillsList(query);
  };

  const handleRemove = async (id: number): Promise<void> => {
    setLoading(true);
    await SkillService.removeITSkill(me.candidate_id, id);
    setLoading(false);
  };

  const handleChange = (values: FormikValues): string => {
    setCurrentFormValues(values);
    return '';
  };

  return (
    <div className="skill">
      <Formik
        enableReinitialize
        onSubmit={handleSubmit}
        initialValues={initialValue}
        validationSchema={validation(t)}
      >
        {({ setFieldValue, isValid, values, errors, setFieldError }) => {
          return (
            <Form>
              <div className="skills scrollable skills-popup-side">
                {skill?.map((item, index: number) => (
                  <div key={index}>
                    <div className="top">
                      <div className="field input-field">
                        <span className="label">{skillType}</span>
                        <div className="field-and-level">
                          <Autocomplete
                            fieldName="skill"
                            value={item.skill.name}
                            placeholder={skillType}
                            show_location={false}
                            className=""
                            onInputChange={(e: string) => {
                              getSkills(e);
                              setFieldValue(
                                `skills[${index}].computerSkill`,
                                e
                              );
                            }}
                            onChange={(value: string) => {
                              const selectedSkill = computerSkills?.find(
                                (skill) => skill.name === value
                              );
                              if (selectedSkill) {
                                setFieldValue(
                                  `skills[${index}].computerSkill`,
                                  selectedSkill.name
                                );
                                setFieldValue(
                                  `skills[${index}].id`,
                                  selectedSkill.id
                                );
                              }
                            }}
                            options={
                              computerSkills?.map(
                                (item: IAutocompleteItem) => ({
                                  id: item.id,
                                  title: item.name
                                })
                              ) || []
                            }
                            values={computerSkills}
                            onFocus={(e) => {
                              getSkills(e);
                            }}
                          />
                        </div>
                      </div>
                      <div className="field">
                        <span
                          className={`label ${
                            errors.skills &&
                            (errors.skills[index] as any)?.proficiency
                              ? 'label-err'
                              : ''
                          }`}
                        >
                          {t('proficiency')}
                        </span>
                        <div className="level">
                          <Level
                            onClick={(level: number) =>
                              setFieldValue(
                                `skills[${index}].proficiency`,
                                level
                              )
                            }
                            isReadonly={false}
                            level={item.proficiency}
                          />
                          {!showAddButton && (
                            <span
                              onClick={() => handleRemove(item.id)}
                              className="remove"
                            >
                              <Image name="trash.svg" />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="field">
                      <span className="label">{t('Description')}</span>
                      <FormField
                        inputClassName="input"
                        fieldName={`skills[${index}].description`}
                        type={InputType.text}
                        placeholder={skillType}
                      />
                      <ErrorMessage
                        className="fmessage"
                        name={`skills[${index}].description`}
                        component="div"
                      />
                    </div>
                  </div>
                ))}
              </div>
              {showAddButton && (
                <div className="add-btn">
                  <ActionButton iconName="plus.svg" onClick={handleAddSkill} />
                  <span className="add-skill">{t('Add Skill')}</span>
                </div>
              )}

              {handleChange(values)}
              <Button
                disabled={loading}
                isLoading={loading}
                t={t}
                type="submit"
                title="Save"
                className={`save ${!showAddButton ? 'no-margin' : ''} ${
                  !isValid ? 'invalid-btn' : ''
                }`}
              />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ITSkills;
